// ============================================================
// THE HITS ON A PLATE, DRAWN (plan body-map H1). Each struck location's
// lines play their signature (a class, keyed so it restarts: PlateFx's
// hitE / hitI / hitX / hitR) under its type's filter, which the plate's
// clock writes as it fades (engine/plate/hits). One filter per type: the
// hits of one attack land together. With motion off the location only
// flashes (the class's still form); no filter, throw or strobe.
// ============================================================

import { Fragment, type CSSProperties, type ReactNode } from 'react';
import clsx from '../../../utils/clsx';
import type { DamageCode } from '../../../engine/impacts';
import { hitFilterAt, strobeAt, type PlateHitPlay, type WaveBand } from '../../../engine/plate/hits';
import { begun, landed as landedOf, latestAt, latestOfType, type BurstHit } from '../../../engine/plate/burst';
import { tripletJolt } from '../../../engine/plate/triplet';
import fx from '../../../styles/PlateFx.module.css';
import type { HitPlay } from '../usePlateHits';
import type { WrittenNode } from './scene';
import { timed } from './timing';

const HIT_CLASS: Record<DamageCode, string> = { E: fx.hitE, I: fx.hitI, X: fx.hitX, R: fx.hitR };

/** A struck location's lines: its signature, heavier for a critical. */
const hitClass = (p: PlateHitPlay) => clsx(HIT_CLASS[p.type], p.crit && fx.crit);

interface StruckProps {
  u: string;
  /** The location. */
  id: string;
  play: HitPlay | null;
  /** The burst's latest moment begun (usePlateHits' `shownAt`). */
  shownAt: number;
  /** Whether the screen plays motion: with it off, no filter - the location only flashes. */
  moving: boolean;
  /** How hard a soaked hit still lands (Daemonic protection; `soakDull`); 1 for none. */
  soak?: number;
  /** Its lines, given the class its hit adds (none when not struck). */
  children: (hitClassName: string) => ReactNode;
}

/**
 * A location's lines, playing its hit if the play struck it: the signature on the lines (keyed by the play, so a new
 * hit restarts it), the type's filter round them - outside the lines' clip, so what it pushes out is never cut at a
 * box.
 */
export function Struck({ u, id, play, shownAt, moving, soak = 1, children }: StruckProps) {
  const landed = play ? landedOf(play.burst.hits) : [];
  const hit = latestAt(landed, id, shownAt);
  if (!play || !hit) return children('');
  return (
    <g
      filter={moving ? `url(#${u}-hit${hit.type})` : undefined}
      style={{ '--amp': (hit.crit ? 2 : 1) * (hit.soaked ? soak : 1) } as CSSProperties}
      data-hit={hit.type}
      data-crit={hit.crit || undefined}
      data-soaked={hit.soaked || undefined}
    >
      <Fragment key={`${play.id}:${landed.indexOf(hit)}`}>
        <Jolt at={moving ? tripletJolt(play.moments, id, hit.at) : null}>{children(hitClass(hit))}</Jolt>
      </Fragment>
    </g>
  );
}

/** Tearing's triplet knocking the struck part (engine/plate/triplet), `at` seconds after the hit; none at null. */
function Jolt({ at, children }: { at: number | null; children: ReactNode }) {
  if (at === null) return children;
  return (
    <g className={fx.tearJolt} style={timed(at)} data-jolt="tearing">
      {children}
    </g>
  );
}

/** The facing label's flare, in the colour of the hit's type. */
const FLARE: Record<DamageCode, string> = { E: fx.flareE, I: fx.flareI, X: fx.flareX, R: fx.flareR };
export const flareClass = (type: DamageCode) => clsx(fx.facingStruck, FLARE[type]);

/**
 * The wave across a struck vehicle: two copies of its lines, white, each seen through a band the clock moves (a
 * clip's rect, never a gradient mask - Chrome caches a mask's paint) and fades.
 */
export function WaveBands({ u }: { u: string }) {
  return [0, 1].map((k) => (
    <g key={k}>
      <clipPath id={`${u}-wave${k}`}>
        <rect data-g={`wave${k}r`} width={0} height={0} />
      </clipPath>
      <g
        className={clsx(fx.wave, k === 1 && fx.waveTrail)}
        clipPath={`url(#${u}-wave${k})`}
        opacity={0}
        data-g={`wave${k}`}
      >
        <use href={`#${u}-art`} />
      </g>
    </g>
  ));
}

/** What the clock writes of the wave: each band's rect and its brightness. */
export const waveNodes = (bands: readonly WaveBand[]): WrittenNode[] =>
  bands.flatMap(({ opacity, ...rect }, k) => [
    { key: `wave${k}r`, attrs: rect },
    { key: `wave${k}`, attrs: { opacity } },
  ]);

/** The damage types that land in a burst (a filter each). */
const typesOf = (hits: readonly BurstHit[]) => [...new Set(landedOf(hits).map((h) => h.type))];

/**
 * What the clock writes `t` seconds into a burst: each type's filter, fading from its latest hit begun (times `k`,
 * the plate's scale: a vehicle's pushes are lighter, doubled for a critical), and (a person's) strobe from its latest
 * critical.
 */
export function hitNodes(hits: readonly BurstHit[], t: number, strobe: boolean, k = 1): WrittenNode[] {
  const filters = typesOf(hits).flatMap((type) => {
    const h = latestOfType(hits, type, t);
    return h ? hitFilterAt(type, (h.crit ? 2 : 1) * k, t - h.at) : [];
  });
  return strobe ? [...filters, strobeNode(hits, t)] : filters;
}

/** A person's strobe: bright, dark, bright from the latest critical begun; nothing otherwise. */
function strobeNode(hits: readonly BurstHit[], t: number): WrittenNode {
  const crit = landedOf(begun(hits, t))
    .filter((h) => h.crit)
    .at(-1);
  const b = crit ? strobeAt(t - crit.at) : null;
  return { key: 'strobe', attrs: { style: b === null ? '' : `filter: brightness(${b})` } };
}

/** The four signatures' filters (the bench's), each drawn at rest - every push at nothing - for the clock to drive. */
const FILTERS: Record<DamageCode, (id: string) => React.JSX.Element> = {
  E: (id) => (
    <filter id={id} x="-30%" y="-30%" width="160%" height="160%">
      <feTurbulence type="fractalNoise" baseFrequency="0.01 0.03" numOctaves={2} seed={4} result="n" />
      <feDisplacementMap
        in="SourceGraphic"
        in2="n"
        scale={0}
        xChannelSelector="R"
        yChannelSelector="G"
        result="d"
        data-g="hitE-d"
      />
      <feGaussianBlur in="d" stdDeviation={0} result="b" data-g="hitE-b" />
      <feMerge>
        <feMergeNode in="b" />
        <feMergeNode in="b" />
        <feMergeNode in="d" />
      </feMerge>
    </filter>
  ),
  I: (id) => (
    <filter id={id} x="-30%" y="-30%" width="160%" height="160%">
      <feOffset in="SourceGraphic" dy={0} result="up" data-g="hitI-up" />
      <feOffset in="SourceGraphic" dy={0} result="down" data-g="hitI-down" />
      <feFlood className={fx.hitEcho} floodOpacity={0.55} result="ink" />
      <feComposite in="ink" in2="up" operator="in" result="a" />
      <feFlood className={fx.hitEcho} floodOpacity={0.4} result="ink2" />
      <feComposite in="ink2" in2="down" operator="in" result="c" />
      <feMerge>
        <feMergeNode in="a" />
        <feMergeNode in="c" />
        <feMergeNode in="SourceGraphic" />
      </feMerge>
    </filter>
  ),
  X: (id) => (
    <filter id={id} x="-40%" y="-40%" width="180%" height="180%">
      <feTurbulence type="turbulence" baseFrequency="0.0035" numOctaves={2} seed={7} result="n" />
      <feDisplacementMap
        in="SourceGraphic"
        in2="n"
        scale={0}
        xChannelSelector="R"
        yChannelSelector="G"
        result="d"
        data-g="hitX-d"
      />
      <feGaussianBlur in="d" stdDeviation={0} result="b" data-g="hitX-b" />
      <feMerge>
        <feMergeNode in="b" />
        <feMergeNode in="d" />
      </feMerge>
    </filter>
  ),
  R: (id) => (
    <filter id={id} x="-30%" y="-10%" width="160%" height="120%">
      <feTurbulence
        type="turbulence"
        baseFrequency="0.00001 0.011"
        numOctaves={1}
        seed={9}
        result="n"
        data-g="hitR-n"
      />
      <feComponentTransfer in="n" result="q">
        <feFuncR type="discrete" tableValues="0 .85 .2 1 .05 .7 .35 .95" />
        <feFuncG type="discrete" tableValues=".5" />
        <feFuncA type="discrete" tableValues="1" />
      </feComponentTransfer>
      <feDisplacementMap
        in="SourceGraphic"
        in2="q"
        scale={0}
        xChannelSelector="R"
        yChannelSelector="G"
        data-g="hitR-d"
      />
    </filter>
  ),
};

/** The filters a play needs: one per damage type in it. */
export function HitDefs({ u, hits }: { u: string; hits: readonly BurstHit[] }) {
  return typesOf(hits).map((type) => <g key={type}>{FILTERS[type](`${u}-hit${type}`)}</g>);
}
